import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';

// ---------------------------------------------------------------------------
// Embedded sub-document types (stored inline within Campaign)
// ---------------------------------------------------------------------------

@Entity()
export class SourceDocumentDoc extends MongoEntity {
  static override _class = 'SourceDocumentDoc';

  @Field(type => String) fileName!: string;
  @Field(type => String) uploadedAt!: string;
  @Field(type => String) url?: string;
  @Field(type => String) source?: string;
}

@Entity()
export class OfferingConfigDoc extends MongoEntity {
  static override _class = 'OfferingConfigDoc';

  @Field(type => String) name!: string;
  @Field(type => String) description!: string;
  @Field(type => [String]) talkingPoints!: string[];
  @Field(type => [SourceDocumentDoc]) sourceDocuments?: SourceDocumentDoc[];
}

@Entity()
export class WeekdayDoc extends MongoEntity {
  static override _class = 'WeekdayDoc';

  @Field(type => Int) day!: number;
  @Field(type => String) displayName!: string;
}

@Entity()
export class ScheduleConfigDoc extends MongoEntity {
  static override _class = 'ScheduleConfigDoc';

  @Field(type => String) timezone!: string;
  @Field(type => String) startTime!: string;
  @Field(type => String) endTime!: string;
  @Field(type => [WeekdayDoc]) sendDays!: WeekdayDoc[];
  @Field(type => Int) dailyLimit!: number;
  @Field(type => Boolean) autoDmOnAccept!: boolean;
}

@Entity()
export class SequenceStepDoc extends MongoEntity {
  static override _class = 'SequenceStepDoc';

  @Field(type => String) stepId!: string;
  @Field(type => String) channel!: string;
  @Field(type => Int) delayValue!: number;
  @Field(type => String) delayUnit!: string;
  @Field(type => String) condition!: string;
  @Field(type => String) prompt!: string;
  @Field(type => String) subject!: string;
  /** Optional AI prompt for generating the email subject line (instead of using the literal `subject` template). */
  @Field(type => String) subjectPrompt?: string;
  /**
   * Manual message mode: when set, this exact text (with {{variables}}
   * interpolated per lead) is the message body — no LLM generation.
   */
  @Field(type => String) manualBody?: string;
  /**
   * Last generated sample message (the wizard step-card preview), persisted so
   * re-opening the campaign shows the same card without a fresh LLM call.
   * `previewKey` fingerprints the content the sample was generated from.
   */
  @Field(type => String) previewSubject?: string;
  @Field(type => String) previewBody?: string;
  @Field(type => String) previewKey?: string;
  /** Refine instruction typed under the preview — kept so the user still sees what they entered. */
  @Field(type => String) refineInstruction?: string;
}

@Entity()
export class LlmModelDoc extends MongoEntity {
  static override _class = 'LlmModelDoc';

  @Field(type => String) llmId!: string;
  @Field(type => String) model!: string;
  @Field(type => String) label!: string;
}

@Entity()
export class GenerationProgressDoc extends MongoEntity {
  static override _class = 'GenerationProgressDoc';

  @Field(type => Int) total!: number;
  @Field(type => Int) done!: number;
  @Field(type => Int) failed!: number;
}

/**
 * Assignment of a Sales Rep to a campaign with a percentage share of leads.
 * The sum of all `percentage` values across a campaign's assignments must be 100.
 * Each Sales Rep has its own subset of email/LinkedIn sending accounts (see
 * `Campaign.emailAccountsByAie` / `linkedinAccountIdsByAie`) and — for email
 * campaigns — its own Instantly sub-campaign (see `Campaign.externalCampaignIds`).
 */
@Entity()
export class AiEmployeeAssignmentDoc extends MongoEntity {
  static override _class = 'AiEmployeeAssignmentDoc';

  @Field(type => String) aiEmployeeId!: string;
  @Field(type => Int) percentage!: number;
}

/** Per-Sales-Rep list of email accounts (sub-document for `emailAccountsByAie`). */
@Entity()
export class AieEmailAccountsDoc extends MongoEntity {
  static override _class = 'AieEmailAccountsDoc';

  @Field(type => String) aiEmployeeId!: string;
  @Field(type => [String]) emailAccounts!: string[];
}

/** Per-Sales-Rep list of LinkedIn account IDs (sub-document for `linkedinAccountIdsByAie`). */
@Entity()
export class AieLinkedinAccountsDoc extends MongoEntity {
  static override _class = 'AieLinkedinAccountsDoc';

  @Field(type => String) aiEmployeeId!: string;
  @Field(type => [String]) linkedinAccountIds!: string[];
}

/** Mapping of one Sales Rep → its Instantly sub-campaign id (used for stats sync). */
@Entity()
export class AieExternalCampaignDoc extends MongoEntity {
  static override _class = 'AieExternalCampaignDoc';

  @Field(type => String) aiEmployeeId!: string;
  @Field(type => String) externalCampaignId!: string;
}

/**
 * Channel-account fallback for cross-channel campaigns.
 *
 * If a Sales Rep is assigned leads but does not have an account for one of the
 * sequence channels, the user can opt to "borrow" another Sales Rep's account so the
 * lead still receives those steps. The persona/identity in the message stays
 * the source Sales Rep — only the sending account is taken from the fallback Sales Rep.
 */
@Entity()
export class AieChannelFallbackDoc extends MongoEntity {
  static override _class = 'AieChannelFallbackDoc';

  /** Sales Rep that owns the leads but is missing accounts for `channel`. */
  @Field(type => String) sourceAiEmployeeId!: string;
  /** Either 'email' or 'linkedin'. */
  @Field(type => String) channel!: string;
  /** Sales Rep whose accounts will be used to send the borrowed channel's steps. */
  @Field(type => String) fallbackAiEmployeeId!: string;
}

// ---------------------------------------------------------------------------
// Campaign entity
// ---------------------------------------------------------------------------

@Entity()
export class Campaign extends MongoEntity {
  static override _class = 'Campaign';
  static override _collectionName = 'campaigns';

  @Field(type => String) userId!: string;
  @Field(type => String) name!: string;
  /** Free-text note about what this campaign is for. Purely informational — never sent to leads or fed to the LLM (that's `offering`). */
  @Field(type => String) description?: string;
  @Field(type => String) campaignType!: string;
  /**
   * TODO: not up to date anymore; need to redo anyway
   * Lifecycle:
   *   draft → generating → ready_to_launch → preparing_to_launch → active ↔ paused → archived | error
   * `ready_to_launch` occurs after "Generate Messages" finishes. Clicking Launch
   * moves to `preparing_to_launch` (1h cooldown, `scheduledLaunchAt` set) rather
   * than launching immediately — the user can still edit (messages, leads,
   * steps, Sales Rep) during that window, which restarts the cooldown; edits
   * that need regeneration transiently flip back to `generating` and return to
   * `preparing_to_launch` (not `ready_to_launch`) once done. The launch-cooldown
   * worker or the "Skip" action executes the actual launch, moving to `active`.
   */
  @Field(type => String) status!: string;
  /** Legacy single Instantly campaign UUID. For multi-AIE campaigns this equals `externalCampaignIds[0]`. */
  @Field(type => String) externalCampaignId?: string;
  /** Per-Sales-Rep Instantly sub-campaign UUIDs (one per Sales Rep assignment, email-only). */
  @Field(type => [AieExternalCampaignDoc]) externalCampaignIds?: AieExternalCampaignDoc[];

  @Field(type => OfferingConfigDoc) offering!: OfferingConfigDoc;
  @Field(type => ScheduleConfigDoc) schedule!: ScheduleConfigDoc;
  @Field(type => [SequenceStepDoc]) sequence!: SequenceStepDoc[];
  @Field(type => LlmModelDoc) llmModel?: LlmModelDoc;
  /** True if the user manually picked `llmModel` (auto-recommend should NOT silently overwrite it). */
  @Field(type => Boolean) llmModelManualOverride?: boolean;

  @Field(type => [String]) emailAccounts!: string[];
  @Field(type => [String]) linkedinAccountIds!: string[];
  /** Legacy single Sales Rep field. New campaigns use `aiEmployeeAssignments`. Kept for backward compatibility (= first assignment). */
  @Field(type => String) aiEmployeeId?: string;

  /**
   * Multiple Sales Reps per campaign with percentage share of leads.
   * If absent or empty, treat as a single 100% assignment using `aiEmployeeId`.
   */
  @Field(type => [AiEmployeeAssignmentDoc]) aiEmployeeAssignments?: AiEmployeeAssignmentDoc[];

  /** Per-Sales-Rep bucket of selected email accounts. Union must equal `emailAccounts`. */
  @Field(type => [AieEmailAccountsDoc]) emailAccountsByAie?: AieEmailAccountsDoc[];
  /** Per-Sales-Rep bucket of selected LinkedIn account ids. Union must equal `linkedinAccountIds`. */
  @Field(type => [AieLinkedinAccountsDoc]) linkedinAccountIdsByAie?: AieLinkedinAccountsDoc[];

  /**
   * Optional cross-AIE channel fallbacks. When an AIE has no account for a
   * channel, the user may delegate that channel's sending to another AIE's
   * account. The persona stays the source AIE — only the sending account is
   * taken from the fallback AIE.
   */
  @Field(type => [AieChannelFallbackDoc]) channelFallbacks?: AieChannelFallbackDoc[];

  /** Whether auto-reply draft generation is enabled for this campaign */
  @Field(type => Boolean) autoReply?: boolean;

  /**
   * Controls how CSV-imported leads are pushed to a connected CRM:
   *   - true  → every CSV lead is upserted into the CRM on each push (manual
   *             button click + automatic post-sync trigger).
   *   - false / undefined → CSV-only leads (no `crmContactId`) are explicitly
   *             skipped during push. Leads that originated from the CRM (e.g.
   *             HubSpot list import) are still pushed/updated either way.
   */
  @Field(type => Boolean) crmAutoSync?: boolean;

  /** Campaign-level prompt for generating AI replies to leads who respond */
  @Field(type => String) replyPrompt?: string;

  /**
   * Outreach language (ISO code: en, de, it, fr, ar). All generated messages,
   * previews and auto-replies are written in this language. Absent = English.
   */
  @Field(type => String) language?: string;

  /** Separate LLM model used for reply generation (may differ from message-generation model) */
  @Field(type => LlmModelDoc) replyLlmModel?: LlmModelDoc;
  /** True if the user manually picked `replyLlmModel`. */
  @Field(type => Boolean) replyLlmModelManualOverride?: boolean;

  @Field(type => Int) leadsProcessed?: number;
  @Field(type => Int) leadsFailed?: number;
  @Field(type => Int) apolloLeadsFilteredNoEmail?: number;

  @Field(type => Int) totalSent?: number;
  @Field(type => Int) totalOpened?: number;
  /**
   * WHERE `totalOpened` came from, and the same distinction `bounceSource`
   * carries: a measured zero is not an unmeasured one.
   *
   * `instantly` — the sync poll read an open timestamp per lead. A reading.
   * `untracked` — the armed path. An open is only observable through a tracking
   *   pixel, and this product deliberately does not put one in the mail
   *   (`docs/unipile-email-api.md`: open tracking was a deliverability gap under
   *   Instantly, and moving to Unipile closes it "by simply not setting it").
   *   So nothing here can ever count an open, and zero means nothing at all.
   * absent — nothing has ever derived this campaign's counters.
   *
   * The value is deliberately NOT 'none': the screen says different things for
   * "nobody has looked" and "nothing can look, on purpose", and only the second
   * stops someone re-enabling tracking to make the number appear.
   */
  @Field(type => String) openSource?: string;
  @Field(type => Int) totalReplied?: number;
  @Field(type => Int) totalBounced?: number;
  /**
   * WHERE `totalBounced` came from — the difference between a measured zero and
   * an unmeasured one, which the screen has no other way to tell.
   *
   * `instantly`  — the sync poll read the provider's per-lead bounce data for
   *   every lead in this campaign. Zero there is a reading.
   * `inbound-notice` — the armed path, where the only source is a
   *   delivery-status notice arriving in the connected mailbox as ordinary mail
   *   (`helpers/email-bounce.ts`). Event-driven, with no way to confirm the far
   *   side sent one, so zero means "none recorded", NEVER "none happened".
   * absent — nothing has ever looked at bounces for this campaign.
   *
   * Only the first licenses printing a zero. See
   * `packages/sdr-ui/src/measured-counters.ts`, which is the single place
   * that decision is made.
   */
  @Field(type => String) bounceSource?: string;

  // LinkedIn-specific campaign-level stats
  @Field(type => Int) totalConnectionsSent?: number;
  /** Leads whose connect step resolved as "already connected" — no invite was ever sent to them. */
  @Field(type => Int) totalConnectionsAlreadyConnected?: number;
  /**
   * Leads whose connect step was skipped because a request to them was already
   * outstanding (LinkedIn: already_invited_recently). Not a send of ours and
   * not a connection — they are waiting on the recipient, so a DM cannot go out.
   */
  @Field(type => Int) totalInvitesPendingElsewhere?: number;
  @Field(type => Int) totalAccepted?: number;
  @Field(type => Int) totalDmSent?: number;

  /** Number of agent-drafted replies that were generated */
  @Field(type => Int) totalDraftsGenerated?: number;

  /** Number of agent-drafted replies that were approved and sent */
  @Field(type => Int) totalRepliesSent?: number;

  /** Number of leads whose sequence is fully complete */
  @Field(type => Int) totalDone?: number;
  /** IDs of Instantly subsequences created for 'replied' email steps */
  @Field(type => [String]) subsequenceIds?: string[];

  @Field(type => String) lastSyncedAt?: string;
  /**
   * When our email content was last pushed to Instantly. The watermark for the
   * re-sync: only messages updated after this need pushing again. Absent means
   * "never pushed since this existed", which re-pushes everything once.
   */
  @Field(type => String) instantlySyncedAt?: string;
  /**
   * When the queued LinkedIn jobs were last brought in step with their messages
   * and their leads' Sales Reps. The same watermark idea as `instantlySyncedAt`,
   * for the other channel: a LinkedIn message edited after this timestamp may
   * still be sitting in a job as its pre-edit text. The integrity worker reads
   * it to decide whether a campaign is worth loading at all.
   */
  @Field(type => String) jobsSyncedAt?: string;
  /**
   * How many delivered emails the last sync examined, and how many of those
   * went out with no subject or no body at all.
   *
   * Instantly resolves an unknown {{variable}} to an empty string and sends the
   * mail anyway, reporting success — so every other counter here can look
   * healthy while recipients received a blank message. This is the only number
   * that contradicts them, and it comes from the delivered mail itself.
   */
  @Field(type => Int) emailDeliveriesChecked?: number;
  @Field(type => Int) emailBlankDeliveries?: number;

  /** Progress of Phase A message generation. Only set while status === 'generating'. */
  @Field(type => GenerationProgressDoc) generationProgress?: GenerationProgressDoc;
  /** Set once when status transitions to 'ready_to_launch'. */
  @Field(type => String) messagesGeneratedAt?: string;

  /**
   * Serialised `ApolloLeadSearchInput` (JSON string) captured at the time the
   * user confirmed Apollo-sourced leads. Lets us re-run the same People Search
   * later to extend the lead pool without forcing the user to re-fill the
   * sourcing form. Only set when the campaign was sourced (even partially)
   * via Apollo.
   */
  @Field(type => String) apolloSearchInputJson?: string;

  /** Absent = belongs to the user's default project (see project-leads.ts). */
  @Field(type => String) projectId?: string;

  /**
   * Set while this campaign is paused *only* because a "Compare Leads" session
   * for a project move is open — holds the id of the campaign being moved
   * (which marks itself too). The resolve step resumes exactly the campaigns
   * carrying this marker, so a campaign the user had paused manually for
   * unrelated reasons is never silently resumed alongside. Cleared on resolve
   * and on cancel.
   */
  @Field(type => String) pausedForCompareWith?: string;

  /**
   * Soft delete. Set to an ISO timestamp when the user deletes the campaign;
   * absent on every live campaign. A soft-deleted campaign is hidden from the
   * dashboard, excluded from project lead dedup, and never sends anything —
   * but all of its data is still there, so "Restore" is a single write.
   * The purge worker (campaign-purge-worker.ts) hard-deletes it for real once
   * `CAMPAIGN_RETENTION_DAYS` have passed.
   *
   * Deliberately a separate field rather than a `status` value: status carries
   * the campaign's position in the send lifecycle, and deleting must not
   * destroy that — otherwise restoring could only ever guess where to put the
   * campaign back.
   */
  @Field(type => String) deletedAt?: string;
  /**
   * The `status` the campaign had at the moment it was deleted, so Restore can
   * put it back where it was. A campaign that was sending is forced dormant on
   * delete (see the delete route), so this may differ from the stored `status`.
   */
  @Field(type => String) statusBeforeDelete?: string;
  /** Same idea for archiving: the status to return to when the user unarchives. */
  @Field(type => String) statusBeforeArchive?: string;
  /** ISO timestamp of the last archive — shown in the UI so "archived" has a date. */
  @Field(type => String) archivedAt?: string;

  @Field(type => String) createdAt!: string;
  @Field(type => String) updatedAt!: string;
  @Field(type => String) launchedAt?: string;
  /**
   * ISO timestamp — set when status is 'preparing_to_launch'. The launch-cooldown
   * worker actually executes the launch once this passes; any edit made during
   * the cooldown pushes it back out to a fresh hour (see resetLaunchCooldownIfPreparing).
   */
  @Field(type => String) scheduledLaunchAt?: string;
}
